<script setup lang="ts">
/**
 * FeatureRow — Z 字形功能卡（text + preview 左右交替）
 * - side="right" 时：text 左 + preview 右
 * - side="left" 时：preview 左 + text 右
 * - highlight=true 时使用蓝色色块突出
 */
import YfbIcon from '@/components/YfbIcon.vue'
import type { IconName } from '@/types/icons'

withDefaults(
  defineProps<{
    title: string
    subtitle: string
    desc: string
    icon: IconName
    side?: 'left' | 'right'
    highlight?: boolean
  }>(),
  {
    side: 'right',
    highlight: false
  }
)
</script>

<template>
  <div
    class="feature-row"
    :class="[
      `feature-row--${side}`,
      { 'feature-row--highlight': highlight }
    ]"
  >
    <div class="feature-row__text">
      <div class="feature-row__eyebrow">
        <span class="feature-row__icon">
          <YfbIcon :name="icon" :size="20" />
        </span>
        <span class="feature-row__subtitle">{{ subtitle }}</span>
      </div>
      <h3 class="feature-row__title">{{ title }}</h3>
      <p class="feature-row__desc">{{ desc }}</p>
    </div>

    <div class="feature-row__preview">
      <slot />
    </div>
  </div>
</template>

<style scoped>
.feature-row {
  display: grid;
  grid-template-columns: 2fr 3fr;
  gap: var(--yfb-sp-7);
  align-items: center;
  padding: var(--yfb-sp-6);
  background-color: var(--yfb-surface);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-lg);
  box-shadow: var(--yfb-shadow-2);
  transition: border-color var(--yfb-anim-fast) ease-out, box-shadow var(--yfb-anim-fast) ease-out;
}

.feature-row:hover {
  border-color: var(--yfb-accent);
  box-shadow: 0 0 14px var(--yfb-glow);
}

.feature-row--highlight {
  background-color: var(--yfb-surface);
  border: 1px solid var(--yfb-accent);
  box-shadow: 0 0 18px var(--yfb-glow);
  position: relative;
}

.feature-row--highlight :deep(.feature-row__eyebrow) {
  background-color: var(--yfb-accent);
  color: var(--yfb-on-accent);
  padding: var(--yfb-sp-1) var(--yfb-sp-2);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-sm);
  align-self: flex-start;
  font-family: var(--yfb-font-heading);
}

.feature-row--highlight :deep(.feature-row__subtitle) {
  color: var(--yfb-on-accent);
  opacity: 1;
  font-weight: 700;
}

.feature-row--highlight :deep(.feature-row__icon) {
  background-color: rgba(11, 11, 16, 0.25);
  color: var(--yfb-on-accent);
}

.feature-row--highlight :deep(.feature-row__title),
.feature-row--highlight :deep(.feature-row__desc) {
  color: var(--yfb-ink);
}

.feature-row--left {
  grid-template-columns: 1fr 1fr;
}

.feature-row__text {
  display: flex;
  flex-direction: column;
  gap: var(--yfb-sp-3);
}

.feature-row__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: var(--yfb-sp-2);
}

.feature-row__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background-color: var(--yfb-surface-2);
  color: var(--yfb-accent);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-sm);
}

.feature-row__subtitle {
  font-size: 10px;
  font-weight: 700;
  font-family: var(--yfb-font-heading);
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-label);
  color: var(--yfb-accent);
}

.feature-row__title {
  font-family: var(--yfb-font-heading);
  font-size: var(--yfb-fs-2xl);
  font-weight: 700;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-display);
  line-height: 1.1;
}

.feature-row__desc {
  font-size: var(--yfb-fs-md);
  font-weight: 400;
  line-height: var(--yfb-lh-lead);
  margin: 0;
  color: var(--yfb-text-secondary);
}

.feature-row__preview {
  min-width: 0;
}

@media (max-width: 1023px) {
  .feature-row {
    grid-template-columns: 1fr;
    gap: var(--yfb-sp-6);
    padding: var(--yfb-sp-6);
  }
}
</style>